/**
 * Real-time Notifications Component
 * Displays real-time notifications and alerts using WebSocket
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AlertTriangle, Info, CheckCircle, X, TrendingDown, Activity } from 'lucide-react';
import { useWebSocket } from '@hooks/useWebSocket';
import toast from 'react-hot-toast';

interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'anomaly';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  data?: any;
}

const RealtimeNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const { connected, on, emit, subscribeToAnomalies } = useWebSocket();

  useEffect(() => {
    if (!connected) return;

    // Subscribe to various real-time events
    subscribeToAnomalies();

    // Listen for notifications
    const cleanup1 = on('notification', (data: any) => {
      const notification: Notification = {
        id: `notif_${Date.now()}_${Math.random()}`,
        type: data.type || 'info',
        title: data.title || 'Notification',
        message: data.message || '',
        timestamp: new Date(data.timestamp),
        read: false,
        data: data.data,
      };

      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);

      // Show toast
      toast(notification.message, {
        icon: getNotificationIcon(notification.type),
      });
    });

    // Listen for anomaly alerts
    const cleanup2 = on('anomaly:alert', (data: any) => {
      const notification: Notification = {
        id: `anomaly_${Date.now()}_${Math.random()}`,
        type: 'anomaly',
        title: '⚠️ Anomaly Detected',
        message: `${data.page}: ${data.metric} - ${data.deviation.toFixed(2)}σ deviation`,
        timestamp: new Date(data.timestamp),
        read: false,
        data,
      };

      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);

      // Show toast for critical anomalies
      if (data.severity === 'critical') {
        toast.error(notification.message, {
          duration: 5000,
          icon: <AlertTriangle className="text-red-600" />,
        });
      }
    });

    // Listen for analytics updates
    const cleanup3 = on('analytics:update', (data: any) => {
      if (data.type === 'significant_change') {
        const notification: Notification = {
          id: `analytics_${Date.now()}_${Math.random()}`,
          type: 'info',
          title: '📊 Analytics Update',
          message: data.message || 'Significant change detected',
          timestamp: new Date(data.timestamp),
          read: false,
          data,
        };

        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
      }
    });

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
    };
  }, [connected, on, subscribeToAnomalies]);

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="text-green-600" size={20} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-600" size={20} />;
      case 'error':
        return <AlertTriangle className="text-red-600" size={20} />;
      case 'anomaly':
        return <TrendingDown className="text-orange-600" size={20} />;
      default:
        return <Info className="text-blue-600" size={20} />;
    }
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount(prev => Math.max(0, prev - 1));
    emit('notification:read', id);
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    emit('notification:read:all');
  };

  const clearNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    markAsRead(id);
  };

  return (
    <div className="relative z-10">
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
        {!connected && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse" />
        )}
      </button>

      {/* Notification Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay - Behind header on mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/10 z-10 lg:hidden"
              onClick={() => setIsOpen(false)}
            />
            {/* Desktop Overlay - Transparent, just to close on outside click */}
            <div className="hidden lg:block fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-16 right-2 sm:absolute sm:right-0 sm:top-12 w-[calc(100vw-1rem)] sm:w-96 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5rem)] sm:max-h-[80vh] bg-white dark:bg-secondary-900 rounded-xl shadow-2xl border border-secondary-200 dark:border-secondary-700 overflow-hidden z-50"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center gap-2">
                  <Activity size={18} className="text-primary-600" />
                  <h3 className="font-semibold">Notifications</h3>
                  {unreadCount > 0 && <span className="badge badge-primary">{unreadCount}</span>}
                </div>
                {notifications.length > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {/* Notifications List */}
              <div className="overflow-y-auto max-h-[60vh]">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-secondary-500">
                    <Bell size={48} className="mx-auto mb-3 opacity-20" />
                    <p>No notifications yet</p>
                    <p className="text-sm mt-1">
                      {connected ? 'Listening for updates...' : 'Connecting...'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-secondary-200 dark:divide-secondary-700">
                    {notifications.map(notification => (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className={`p-4 hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors cursor-pointer ${
                          !notification.read ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''
                        }`}
                        onClick={() => markAsRead(notification.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-1">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className="font-medium text-sm">{notification.title}</p>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  clearNotification(notification.id);
                                }}
                                className="flex-shrink-0 p-1 rounded hover:bg-secondary-200 dark:hover:bg-secondary-700 transition-colors"
                                aria-label="Dismiss"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-secondary-500 mt-2">
                              {notification.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="p-3 border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
                  <button
                    onClick={() => {
                      setNotifications([]);
                      setUnreadCount(0);
                      setIsOpen(false);
                    }}
                    className="text-xs text-red-600 hover:underline w-full text-center"
                  >
                    Clear all notifications
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RealtimeNotifications;
